/** @jsxImportSource @emotion/react */
import { useState } from 'react'
import styled from '@emotion/styled'
import { ActionButton } from 'components/widgets'
import { ConfirmationDialog } from 'components/partials'
import { trans, TranslationGroup } from 'lib/types'
import { KnownBluetoothDevice } from 'lib/types/bluetoothPairing'
import { getAppColors } from 'lib/constants'

const Wrapper = styled.div({
  marginBottom: '4rem',
})

const SectionTitle = styled.h3({
  fontFamily: 'Montserrat',
  fontWeight: 'bold',
  fontSize: '1.4rem',
  margin: '0 0 1.2rem 0',
})

const Table = styled.table({
  width: '100%',
  borderCollapse: 'collapse',
  fontFamily: 'Montserrat',
  fontSize: '1.3rem',
})

const Th = styled.th({
  textAlign: 'left',
  padding: '0.8rem 1.2rem',
  borderBottom: `1px solid ${getAppColors('--color-gray-2')}`,
  fontWeight: 600,
  color: getAppColors('--color-secondary'),
})

const Td = styled.td({
  padding: '0.8rem 1.2rem',
  borderBottom: `1px solid ${getAppColors('--color-gray-4')}`,
})

const MakeTh = styled(Th)({
  width: '25%',
})

const ActionTh = styled(Th)({
  textAlign: 'right',
  width: '1%',
  whiteSpace: 'nowrap',
})

const ActionTd = styled(Td)({
  textAlign: 'right',
  width: '1%',
  whiteSpace: 'nowrap',
})

const EmptyRow = styled.div({
  padding: '2.4rem 1.2rem',
  textAlign: 'center',
  color: '#999',
  fontStyle: 'italic',
})

type Props = {
  devices: KnownBluetoothDevice[]
  onRemove: (id: string) => void
  disabled?: boolean
}

export const KnownDevicesTable = ({ devices, onRemove, disabled }: Props) => {
  const settings: TranslationGroup = trans.settings()
  const [pendingRemoveId, setPendingRemoveId] = useState<string | null>(null)

  const confirm = () => {
    if (pendingRemoveId) onRemove(pendingRemoveId)
    setPendingRemoveId(null)
  }

  return (
    <Wrapper>
      <SectionTitle>{settings.known_devices}</SectionTitle>
      {devices.length === 0 ? (
        <EmptyRow>{settings.no_known_devices}</EmptyRow>
      ) : (
        <Table>
          <thead>
            <tr>
              <MakeTh>{settings.device_name}</MakeTh>
              <ActionTh />
            </tr>
          </thead>
          <tbody>
            {devices.map(d => (
              <tr key={d.id}>
                <Td>{d.name}</Td>
                <ActionTd>
                  <ActionButton width="90px" height="28px" onClick={() => setPendingRemoveId(d.id)} disabled={disabled}>
                    {settings.device_remove}
                  </ActionButton>
                </ActionTd>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      <ConfirmationDialog
        title={settings.device_remove}
        description={settings.confirm_remove_known_device}
        open={!!pendingRemoveId}
        onConfirm={confirm}
        handleClose={() => setPendingRemoveId(null)}
      />
    </Wrapper>
  )
}
